import { useState } from "react";

import { ChevronRight, FileText, Globe, Lightbulb, Shield, Zap as ZapIcon } from "lucide-react";

import { hasAdvisoryAii } from "../utils/category";
import ConfidenceMeter from "./ConfidenceMeter";
import ScoreGauge from "./ScoreGauge";

// Schema validation ladder (Evidence auditability §A).
const SCHEMA_TIERS = [
  { key: "detected", label: "Detected" },
  { key: "valid", label: "Valid" },
  { key: "content_matched", label: "Matched" },
  { key: "commercial", label: "Commercial" },
];

// Rating → number transparency (Evidence auditability §D).
const RATING_POINTS = { absent: 0, weak: 25, partial: 50, strong: 75, excellent: 100 };

const SchemaTierLadder = ({ record }) => {
  const reachedIndex = SCHEMA_TIERS.findIndex((t) => t.key === record.tier_reached);
  return (
    <div className="bg-surface-container-high rounded-lg p-3 mb-2">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-on-surface">{record.type}</span>
        <span className="text-xs text-on-surface-variant">
          {record.deprecated ? "Deprecated · 0 pts" : `+${record.credited_points} pts`}
        </span>
      </div>
      <div className="flex items-center gap-1 mb-2">
        {SCHEMA_TIERS.map((tier, i) => {
          const reached = !record.deprecated && i <= reachedIndex;
          return (
            <span
              key={tier.key}
              className={`flex-1 text-center px-1 py-0.5 rounded text-[10px] font-semibold ${
                reached
                  ? "bg-green-600/25 text-green-500"
                  : "bg-surface-container-low text-on-surface-variant"
              }`}
            >
              {tier.label}
            </span>
          );
        })}
      </div>
      {record.deprecated ? (
        <p className="text-xs text-yellow-600">
          No longer yields a rich result — earns no score (safe to keep for other engines).
        </p>
      ) : (
        <div className="text-xs text-on-surface-variant space-y-1">
          {record.missing_props?.length > 0 && (
            <p>
              <span className="text-red-400">Missing required:</span>{" "}
              {record.missing_props.join(", ")}
            </p>
          )}
          {record.unmatched_props?.length > 0 && (
            <p>
              <span className="text-yellow-600">Not found on page:</span>{" "}
              {record.unmatched_props.join(", ")}
            </p>
          )}
          {record.next_step && <p className="text-on-surface">Next: {record.next_step}</p>}
        </div>
      )}
    </div>
  );
};

// Content accessibility for AI crawlers (raw-HTML, no JS).
const FetchQualityNote = ({ fetchQuality }) => {
  const map = {
    clean: {
      cls: "bg-green-600/20 text-green-600",
      label: "Content reachable without JS",
      detail: "Real content is served in plain HTML — visible to AI answer crawlers.",
    },
    js_dependent: {
      cls: "bg-yellow-600/20 text-yellow-600",
      label: "JS-dependent content",
      detail:
        "Content appears only after JavaScript renders. AI answer crawlers fetch raw HTML and don't run JS, so they may see little — this drags the crawlability score down.",
    },
    blocked: {
      cls: "bg-red-600/20 text-red-600",
      label: "Blocked / inaccessible",
      detail:
        "The page returned a captcha/challenge or error even after fallback — crawlers can't read it.",
    },
    unmeasured: {
      cls: "bg-surface-container-high text-on-surface-variant",
      label: "Not measured",
      detail:
        "Content accessibility wasn't measured for this audit — re-run to get crawl-quality data.",
    },
  };
  const info = map[fetchQuality] || map.unmeasured;
  return (
    <div className="bg-surface-container-low rounded-xl p-4">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-semibold text-on-surface">Content accessibility</h4>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${info.cls}`}>
          {info.label}
        </span>
      </div>
      <p className="text-sm text-on-surface-variant">{info.detail}</p>
    </div>
  );
};

// What the audit actually looked at (Evidence auditability §B/§C).
const CrawlProvenanceCard = ({ crawlProvenance }) => {
  if (!crawlProvenance) {
    return (
      <div className="bg-surface-container-low rounded-xl p-4 text-sm text-on-surface-variant">
        Crawl details aren't available for audits run before this feature — re-run to see which
        pages were analysed.
      </div>
    );
  }
  const {
    pages_crawled: pagesCrawled = [],
    pages_blocked: pagesBlocked = [],
    languages_detected: languagesDetected = [],
    language_audited: languageAudited,
    content_snapshot: contentSnapshot,
    audit_confidence: auditConfidence = "",
  } = crawlProvenance;
  return (
    <div className="bg-surface-container-low rounded-xl p-4">
      <h4 className="font-semibold text-on-surface mb-2">Pages analysed</h4>
      {auditConfidence && <ConfidenceMeter level={auditConfidence} className="mb-3" />}
      {contentSnapshot?.captured_at && !isNaN(new Date(contentSnapshot.captured_at).getTime()) && (
        <p className="text-xs text-on-surface-variant mb-2">
          Evidence is a snapshot captured at{" "}
          {new Date(contentSnapshot.captured_at).toLocaleString()}, not the live site.
        </p>
      )}
      <div className="max-h-40 overflow-auto text-sm text-on-surface-variant space-y-1">
        {pagesCrawled.map((page) => (
          <div key={page.url} className="flex items-center justify-between gap-2">
            <span className="truncate">{page.url}</span>
            <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-container-high">
              {page.fetch_method}
            </span>
          </div>
        ))}
      </div>
      {pagesBlocked.length > 0 && (
        <p className="text-xs text-red-400 mt-2">
          {pagesBlocked.length} page(s) blocked: {pagesBlocked.map((p) => p.url).join(", ")}
        </p>
      )}
      {languagesDetected.length > 0 && (
        <p className="text-xs text-on-surface-variant mt-2">
          Languages detected: {languagesDetected.join(", ")} — audited{" "}
          {languageAudited || "the default language"}; other variants weren't separately scored.
        </p>
      )}
    </div>
  );
};

// Sub-factor points breakdown per category. When the important pages weren't
// adequately reached (coverage-gated sub-factor display), the granular per-sub-factor
// ratings are withheld and only the caveated category number is shown.
const SubFactorPoints = ({ reportDetails, categoryId, suppressed, caveat }) => {
  const subFactors = reportDetails[categoryId]?.sub_factors || [];
  if (subFactors.length === 0) return null;
  if (suppressed) {
    return (
      <div className="bg-surface-container-low rounded-xl p-3 text-xs text-on-surface-variant">
        Assessed from limited coverage — sub-factor detail withheld.
        {caveat ? ` ${caveat}.` : ""}
      </div>
    );
  }
  return (
    <details className="bg-surface-container-low rounded-xl p-3">
      <summary className="cursor-pointer text-sm font-medium text-primary">
        How this score was computed
      </summary>
      <p className="text-xs text-on-surface-variant mt-2">
        The qualitative rating sets the band; on-page extractability (in structured markup vs flat
        prose) positions the score within it. The category score is the mean of these.
      </p>
      <div className="mt-2 space-y-1">
        {subFactors.map((sf) => (
          <div key={sf.factor} className="flex items-center justify-between text-sm">
            <span className="text-on-surface-variant">{sf.factor}</span>
            <span className="text-on-surface font-semibold">
              {sf.rating}
              {sf.extractability ? ` · ${sf.extractability}` : ""} →{" "}
              {RATING_POINTS[sf.rating] ?? "—"}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
};

const DetailedAnalysisView = ({ ref, data, technicalAnalysisData }) => {
  const [openSection, setOpenSection] = useState("AEO");
  const categoryScores = data?.category_scores || {};
  const reportDetails = data?.report_details || {};
  const scoreBreakdown = data?.score_breakdown || null;
  const safeTechnicalAnalysisData = technicalAnalysisData || {
    technical_score: 0,
    robots_data: {
      content: "",
      is_found: false,
      error: "",
      bot_permissions: {},
    },
    sitemap_data: { is_found: false, error: "", found_sitemap_url: null },
    schema_data: {
      is_found: false,
      types: [],
      block_count: 0,
      total_properties: 0,
      error: "",
    },
    llms_txt_data: {
      is_found: false,
      found_url: null,
      content_preview: "",
      error: "",
    },
    pagespeed_data: null,
  };
  const sections = [
    {
      id: "AEO",
      title: "AEO - Answer Engine",
      score: categoryScores.AEO,
      band: categoryScores.AEO_band,
      icon: <FileText className="w-5 h-5 text-on-surface-variant" />,
    },
    {
      id: "GEO",
      title: "GEO - Generative Engine",
      score: categoryScores.GEO,
      band: categoryScores.GEO_band,
      icon: <Globe className="w-5 h-5 text-on-surface-variant" />,
    },
    {
      id: "AIO",
      title: "AIO - AI Overviews",
      subtitle: "Google AI Overviews optimisation",
      score: categoryScores.AIO,
      band: categoryScores.AIO_band,
      icon: <Lightbulb className="w-5 h-5 text-on-surface-variant" />,
    },
    // AI Crawlability (formerly "Technical SEO") inserted after AIO
    {
      id: "TechnicalSEO",
      title: "AI Crawlability",
      subtitle: "Crawler access & indexing — not page speed",
      score: safeTechnicalAnalysisData.technical_score,
      band: safeTechnicalAnalysisData.technical_score_band,
      icon: <Shield className="w-5 h-5 text-on-surface-variant" />,
    },
    {
      id: "SXO",
      title: "SXO - Search Experience",
      score: categoryScores.SXO,
      band: categoryScores.SXO_band,
      icon: <ZapIcon className="w-5 h-5 text-on-surface-variant" />,
    },
    // AI Integration is advisory only — rendered (when present) but NOT part of
    // the overall score. Hidden for legacy audits that never measured it.
    ...(hasAdvisoryAii(categoryScores)
      ? [
          {
            id: "AII",
            title: "AII - AI Integration",
            subtitle: "Advisory — not part of your overall score",
            advisory: true,
            score: categoryScores.AII,
            band: categoryScores.AII_band,
            icon: <Lightbulb className="w-5 h-5 text-on-surface-variant" />,
          },
        ]
      : []),
  ];
  const {
    content: robotsText,
    is_found: robotsFound,
    error: robotsParseError,
    bot_permissions: botPermissions = {},
  } = safeTechnicalAnalysisData.robots_data || {};
  const {
    is_found: sitemapFound,
    error: sitemapFetchError,
    found_sitemap_url,
  } = safeTechnicalAnalysisData.sitemap_data || {};
  const {
    is_found: schemaFound,
    types: schemaTypes = [],
    block_count: schemaBlockCount = 0,
    total_properties: schemaTotalProperties = 0,
    type_records: schemaTypeRecords = [],
    credited_points: schemaCreditedPoints = 0,
    error: schemaError,
  } = safeTechnicalAnalysisData.schema_data || {};
  // Content-accessibility + crawl provenance (Evidence auditability §B/§C).
  const fetchQuality = safeTechnicalAnalysisData.fetch_quality || "unmeasured";
  const crawlProvenance = safeTechnicalAnalysisData.crawl_provenance || null;
  // When important pages weren't adequately reached, withhold the granular
  // per-sub-factor ratings (show only the caveated category number).
  const suppressSubFactorDetail = Boolean(crawlProvenance?.suppress_subfactor_detail);
  const confidenceCaveat = crawlProvenance?.confidence_caveat || "";
  // Gateway state: deliberate bot-blocks on important pages subtract an explicit
  // penalty from the headline — surfaced in the AI Crawlability card.
  const gatewayFailing = Boolean(scoreBreakdown?.gateway_failing);
  const blockPenalty = scoreBreakdown?.block_penalty;
  const importantPagesBlocked = scoreBreakdown?.important_pages_blocked;
  const importantPagesTotal = scoreBreakdown?.important_pages_total;
  const {
    is_found: llmsTxtFound,
    found_url: llmsTxtUrl,
    content_preview: llmsTxtPreview,
    error: llmsTxtError,
  } = safeTechnicalAnalysisData.llms_txt_data || {};
  // Real Core Web Vitals from PageSpeed Insights (mobile + desktop). Null when
  // PSI is disabled or both strategy fetches failed — the panel degrades to a
  // short "unavailable" note in that case.
  const pagespeed = safeTechnicalAnalysisData.pagespeed_data || null;

  const AuditSection = ({ id, title, subtitle, icon, score, band, advisory, children }) => (
    <div className="border border-surface-container-high rounded-xl">
      <button
        onClick={() => setOpenSection(openSection === id ? null : id)}
        className="w-full flex items-center justify-between p-4 bg-surface-container-lowest hover:bg-surface-container-low rounded-xl"
      >
        <div className="flex items-center gap-3">
          {icon}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-on-surface">{title}</h3>
              {advisory && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-container-high text-on-surface-variant">
                  Advisory
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-on-surface-variant">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <ScoreGauge score={score} size="medium" band={band} />
          <ChevronRight
            className={`w-5 h-5 text-on-surface-variant transition-transform ${openSection === id ? "rotate-90" : ""}`}
          />
        </div>
      </button>
      {openSection === id && (
        <div className="p-6 bg-surface-container-low rounded-b-xl">{children}</div>
      )}
    </div>
  );

  //  UPDATED Component: BotBadge with theme classes
  const BotBadge = ({ name, status }) => {
    const base =
      status === "allowed"
        ? "bg-green-600/20 text-green-600"
        : status === "disallowed"
          ? "bg-red-600/20 text-red-600"
          : "bg-surface-container-high text-on-surface-variant";
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${base} mr-2 mb-2`}
      >
        {name}
      </span>
    );
  };

  //  UPDATED Component: RobotsCard with theme classes
  const RobotsCard = () => {
    return (
      <div className="bg-surface-container-low rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-on-surface">Robots.txt Rules</h4>
          <span className={`text-xs ${robotsFound ? "text-green-400" : "text-red-400"}`}>
            {robotsFound ? "Found" : "Not Found"}
          </span>
        </div>

        <div className="mb-3 text-sm text-on-surface-variant">
          {robotsFound === false && (
            <div className="text-red-400">robots.txt not found or fetch failed.</div>
          )}
          {robotsFound === true && robotsText && (
            <div className="bg-surface-container-high whitespace-pre-wrap max-h-40 overflow-auto p-3 rounded">
              <pre className="text-xs">{robotsText}</pre>
            </div>
          )}
          {robotsParseError && (
            <div className="text-yellow-400 mt-2">Parse error: {robotsParseError}</div>
          )}
        </div>

        <div>
          <p className="text-sm mb-2 text-on-surface-variant">AI Agent Permissions</p>
          <div className="flex flex-wrap">
            {Object.entries(botPermissions).map(([b, status]) => (
              <BotBadge key={b} name={b} status={status || "unknown"} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  //  UPDATED Component: SitemapCard with theme classes
  const SitemapCard = () => {
    return (
      <div className="bg-surface-container-low rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-on-surface">Sitemap.xml Analysis</h4>
          <span className={`text-xs ${sitemapFound ? "text-green-400" : "text-red-400"}`}>
            {sitemapFound ? "Found" : "Not Found"}
          </span>
        </div>
        <div className="text-sm text-on-surface-variant">
          {sitemapFound === false && (
            <div className="text-red-400">
              Sitemap URL not found, could not be fetched, or invalid.
            </div>
          )}
          {sitemapFound === true && (
            <div className="text-on-surface-variant">
              Sitemap found at <span className="text-primary">{found_sitemap_url}</span>
            </div>
          )}
          {sitemapFetchError && <div className="text-yellow-400 mt-2">{sitemapFetchError}</div>}
        </div>
      </div>
    );
  };

  //  Component: SchemaCard — graded validation ladder per detected type
  const SchemaCard = () => {
    return (
      <div className="bg-surface-container-low rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-on-surface">Structured Data (JSON-LD)</h4>
          <span className={`text-xs ${schemaFound ? "text-green-400" : "text-red-400"}`}>
            {schemaFound ? `Found · +${schemaCreditedPoints} pts` : "Not Found"}
          </span>
        </div>
        <p className="text-xs text-on-surface-variant mb-3">
          Each type is graded detected → valid (required properties) → content-matched (values
          appear on the page) → commercially-relevant. Validation is structural, not a
          Google/schema.org check.
        </p>
        <div className="text-sm text-on-surface-variant">
          {schemaFound === false && (
            <div className="text-red-400">No structured data detected.</div>
          )}
          {schemaFound === true && (
            <>
              {schemaTypeRecords.length > 0 ? (
                <div className="mb-3">
                  {schemaTypeRecords.map((record, i) => (
                    <SchemaTierLadder key={`${record.type}-${i}`} record={record} />
                  ))}
                </div>
              ) : (
                schemaTypes.length > 0 && (
                  <div className="mb-3 flex flex-wrap">
                    {schemaTypes.map((type) => (
                      <span
                        key={type}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-surface-container-high text-on-surface-variant mr-2 mb-2"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                )
              )}
              <div className="flex gap-6">
                <div>
                  <p className="text-xs text-on-surface-variant">Blocks</p>
                  <p className="text-lg font-semibold text-on-surface">{schemaBlockCount}</p>
                </div>
                <div>
                  <p className="text-xs text-on-surface-variant">Properties</p>
                  <p className="text-lg font-semibold text-on-surface">{schemaTotalProperties}</p>
                </div>
              </div>
            </>
          )}
          {schemaError && <div className="text-red-400 mt-2">{schemaError}</div>}
        </div>
      </div>
    );
  };

  //  Component: LlmsTxtCard with theme classes
  const LlmsTxtCard = () => {
    return (
      <div className="bg-surface-container-low rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-on-surface">llms.txt</h4>
          <span className={`text-xs ${llmsTxtFound ? "text-green-400" : "text-red-400"}`}>
            {llmsTxtFound ? "Found" : "Not Found"}
          </span>
        </div>
        <div className="text-sm text-on-surface-variant">
          {llmsTxtFound === false && (
            <div className="text-red-400">llms.txt not found or fetch failed.</div>
          )}
          {llmsTxtFound === true && (
            <>
              {llmsTxtUrl && (
                <div className="mb-3 text-on-surface-variant">
                  Found at{" "}
                  <a
                    href={llmsTxtUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {llmsTxtUrl}
                  </a>
                </div>
              )}
              {llmsTxtPreview && (
                <div className="bg-surface-container-high whitespace-pre-wrap max-h-40 overflow-auto p-3 rounded">
                  <pre className="text-xs">{llmsTxtPreview}</pre>
                </div>
              )}
            </>
          )}
          {llmsTxtError && <div className="text-red-400 mt-2">{llmsTxtError}</div>}
        </div>
      </div>
    );
  };

  // Core Web Vitals thresholds (web.dev). Returns good | needs-improvement | poor.
  const cwvThresholds = {
    lcp: [2500, 4000],
    fcp: [1800, 3000],
    inp: [200, 500],
    tbt: [200, 600],
    cls: [0.1, 0.25],
  };
  const cwvStatus = (metric, value) => {
    if (value === null || value === undefined) return "unknown";
    const t = cwvThresholds[metric];
    if (!t) return "unknown";
    if (value <= t[0]) return "good";
    if (value <= t[1]) return "needs-improvement";
    return "poor";
  };
  const cwvStatusClass = {
    good: "bg-green-600/20 text-green-600",
    "needs-improvement": "bg-yellow-600/20 text-yellow-600",
    poor: "bg-red-600/20 text-red-600",
    unknown: "bg-surface-container-high text-on-surface-variant",
  };
  const cwvStatusLabel = {
    good: "Good",
    "needs-improvement": "Needs work",
    poor: "Poor",
    unknown: "N/A",
  };
  const cwvFormat = {
    lcp: (v) => `${(v / 1000).toFixed(2)} s`,
    fcp: (v) => `${(v / 1000).toFixed(2)} s`,
    inp: (v) => `${Math.round(v)} ms`,
    tbt: (v) => `${Math.round(v)} ms`,
    cls: (v) => v.toFixed(3),
  };

  const MetricTile = ({ label, metric, value }) => {
    const status = cwvStatus(metric, value);
    const hasValue = value !== null && value !== undefined;
    return (
      <div className="bg-surface-container-high rounded-lg p-3">
        <p className="text-xs text-on-surface-variant">{label}</p>
        <p className="text-lg font-semibold text-on-surface">
          {hasValue ? cwvFormat[metric](value) : "—"}
        </p>
        {hasValue && (
          <span
            className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cwvStatusClass[status]}`}
          >
            {cwvStatusLabel[status]}
          </span>
        )}
      </div>
    );
  };

  const StrategyMetrics = ({ strategy }) => {
    if (!strategy) {
      return <p className="text-sm text-on-surface-variant">No data available for this device.</p>;
    }
    const isField = strategy.source === "field";
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-4">
          <ScoreGauge
            score={strategy.performance_score}
            size="medium"
            band={strategy.performance_score_band}
          />
          <div>
            <p className="text-sm font-semibold text-on-surface">Performance Score</p>
            <span
              className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                isField
                  ? "bg-green-600/20 text-green-600"
                  : "bg-surface-container-high text-on-surface-variant"
              }`}
              title={
                isField
                  ? "Real-user data from the Chrome UX Report"
                  : "Lab data simulated by Lighthouse"
              }
            >
              {isField ? "Field (CrUX)" : "Lab (Lighthouse)"}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <MetricTile label="LCP" metric="lcp" value={strategy.lcp_ms} />
          <MetricTile label="CLS" metric="cls" value={strategy.cls} />
          <MetricTile label="INP" metric="inp" value={strategy.inp_ms} />
          <MetricTile label="FCP" metric="fcp" value={strategy.fcp_ms} />
          <MetricTile label="TBT" metric="tbt" value={strategy.tbt_ms} />
        </div>
      </div>
    );
  };

  const CoreWebVitalsPanel = () => {
    if (!pagespeed || (!pagespeed.mobile && !pagespeed.desktop)) {
      return (
        <div className="bg-surface-container-low rounded-xl p-4 text-sm text-on-surface-variant">
          Core Web Vitals are unavailable for this audit — PageSpeed Insights data could not be
          fetched.
        </div>
      );
    }
    return (
      <div className="bg-surface-container-low rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-on-surface">Core Web Vitals</h4>
          <span className="text-xs text-on-surface-variant">
            Mobile-first · drives the SXO score
          </span>
        </div>
        <StrategyMetrics strategy={pagespeed.mobile} />
        {pagespeed.desktop && (
          <details className="mt-4">
            <summary className="cursor-pointer text-sm font-medium text-primary">
              Desktop details
            </summary>
            <div className="mt-3">
              <StrategyMetrics strategy={pagespeed.desktop} />
            </div>
          </details>
        )}
      </div>
    );
  };

  // Rating → number transparency (Evidence auditability §D).

  return (
    <div ref={ref} className="space-y-4">
      {sections.map((section) => (
        <AuditSection
          key={section.id}
          id={section.id}
          title={section.title}
          subtitle={section.subtitle}
          icon={section.icon}
          score={section.score}
          band={section.band}
          advisory={section.advisory}
        >
          {/* For TechnicalSEO, show the robots + sitemap cards */}
          {section.id === "TechnicalSEO" ? (
            <div className="space-y-4">
              {gatewayFailing && (
                <div className="rounded-lg border border-red-500/40 bg-red-600/10 p-3 text-sm text-red-400">
                  <p className="font-semibold">AI Crawlability is the gateway to AI visibility.</p>
                  <p className="mt-1 text-on-surface-variant">
                    Important pages are deliberately blocked to AI search/answer bots, which
                    subtracts an explicit penalty from your overall score
                    {typeof blockPenalty === "number" && blockPenalty > 0
                      ? ` (currently −${blockPenalty} points`
                      : ""}
                    {typeof blockPenalty === "number" &&
                    blockPenalty > 0 &&
                    typeof importantPagesBlocked === "number" &&
                    typeof importantPagesTotal === "number" &&
                    importantPagesTotal > 0
                      ? `, from ${importantPagesBlocked} of ${importantPagesTotal} important pages blocked)`
                      : typeof blockPenalty === "number" && blockPenalty > 0
                        ? ")"
                        : ""}
                    . Allow the AI search/answer bots in <code>robots.txt</code> and serve real
                    content in raw HTML.
                  </p>
                </div>
              )}
              <FetchQualityNote fetchQuality={fetchQuality} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RobotsCard />
                <SitemapCard />
                <SchemaCard />
                <LlmsTxtCard />
              </div>
              <CrawlProvenanceCard crawlProvenance={crawlProvenance} />
              <div className="text-sm text-on-surface-variant mt-3">
                <p>
                  AI Crawlability measures crawler access &amp; indexing —{" "}
                  <strong>not page speed</strong>. Page performance (Core Web Vitals) is measured
                  separately under SXO. Ensure your robots.txt allows the AI bots (e.g. GPTBot,
                  OAI-SearchBot, PerplexityBot) and that your sitemap.xml is accessible.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {section.id === "SXO" && <CoreWebVitalsPanel />}
              <SubFactorPoints
                reportDetails={reportDetails}
                categoryId={section.id}
                suppressed={suppressSubFactorDetail}
                caveat={confidenceCaveat}
              />
              <p className="font-semibold text-on-surface-variant">Issues:</p>
              <ul className="list-disc list-inside text-on-surface-variant space-y-1">
                {(reportDetails[section.id]?.issues || []).map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
              <p className="font-semibold text-on-surface-variant mt-4">Recommendations:</p>
              <ul className="list-disc list-inside text-on-surface-variant space-y-1">
                {(reportDetails[section.id]?.recommendations || []).map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </AuditSection>
      ))}
    </div>
  );
};

export default DetailedAnalysisView;
